import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { sendEvent } from 'utils/Event';
import Box from '@mui/material/Box';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import Typography from '@mui/material/Typography';
import { CustomSortableHeader } from 'utils/Datagrid/CustomGenericDataGridUtils';
import {
  CustomHeaderWithoutSort,
  datagridMappingTheme,
} from 'utils/Datagrid/CustomCultureDataGridUtils';
import {
  editCultureMappingData,
  getCultureMappingDataByTypeAndStructure,
  getCultures,
} from 'services/API/Cooperative';
import { IMPORT_TYPE } from 'utils/GlobalVariables';
import { CustomToolBarMappingDatagrid } from 'utils/Datagrid/CustomToolBarMappingDatagrid';
import useDataGridMappingActions from 'utils/Datagrid/useDataGridMappingActions';
import CustomCultureDataGrid from 'utils/Datagrid/CustomCultureDataGrid';
import { getOnlyAttributeUpdatedBetweenObjects } from 'utils/tools_functions';
import AddCultureMappingDataModal from './AddCultureMappingDataModal';
import ImportMappingFileModal from './ImportMappingFileModal';
import { TextField } from '@mui/material';
import AccordionErrorMappingImport from './AccordionErrorMappingImport';
import { MappingImportResponseContext } from 'utils/context';

const MappingCulture = ({ cultureList, cooperativeId, mappingTypes, setCultureList }) => {
  const { t } = useTranslation();
  const { cultureMappingData, allMappingData, setAllMappingData, cultureMappingImportResponse } =
    useContext(MappingImportResponseContext);
  const [selectedMappingType, _setSelectedMappingType] = useState(
    allMappingData?.mappingCulture?.lastSelectedMappingType,
  );
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isModalCreateOpen, setModalCreateOpen] = useState(false);
  const [sortModel, setSortModel] = useState([
    {
      field: 'externalName',
      sort: 'asc',
    },
  ]);

  const setSelectedMappingType = (importType) => {
    _setSelectedMappingType(importType);
    setAllMappingData((prev) => ({
      ...prev,
      mappingCulture: {
        ...prev.mappingCulture,
        lastSelectedMappingType: importType,
      },
    }));
  };

  useEffect(() => {
    if (cultureMappingData === null) {
      fetchAllData();
    }
    if (cultureList === null) {
      getCultures(cooperativeId, false)
        .then((resp) => {
          setCultureList([
            ...resp.data.data.map((culture) => ({
              label: culture.name,
              value: culture.id,
            })),
            { label: '', value: '' },
          ]);
        })
        .catch((err) => toast.error(err?.data?.message));
    }
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  useEffect(() => {
    if (selectedMappingType !== null) {
      handleMappingTypeChange(selectedMappingType);
    } else {
      if (mappingTypes?.length > 0) {
        handleMappingTypeChange(mappingTypes[0]);
      } else {
        handleMappingTypeChange(IMPORT_TYPE.geofolia);
      }
    }
  }, [cultureMappingData]);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        getCultureMappingDataByTypeAndStructure(IMPORT_TYPE.telepac, cooperativeId),
        getCultureMappingDataByTypeAndStructure(IMPORT_TYPE.apia, cooperativeId),
        getCultureMappingDataByTypeAndStructure(IMPORT_TYPE.wiuz, cooperativeId),
        getCultureMappingDataByTypeAndStructure(IMPORT_TYPE.geofolia, cooperativeId),
      ]).then((res) => {
        const telepacMappingData = res?.[0]?.data?.data || [];
        const apiaMappingData = res?.[1]?.data?.data || [];
        const wiuzMappingData = res?.[2]?.data?.data || [];
        const geofoliaMappingData = res?.[3]?.data?.data || [];

        const cultureMappingData = {
          [IMPORT_TYPE.telepac]: telepacMappingData,
          [IMPORT_TYPE.apia]: apiaMappingData,
          [IMPORT_TYPE.wiuz]: wiuzMappingData,
          [IMPORT_TYPE.geofolia]: geofoliaMappingData,
        };
        setAllMappingData((prev) => ({
          ...prev,
          mappingCulture: {
            ...prev.mappingCulture,
            data: cultureMappingData,
          },
        }));
      });
    } catch (error) {
      toast.error(error?.message);
    }
  };

  const handleMappingTypeChange = (importType) => {
    resetRowModesModel();
    setSelectedMappingType(importType);
  };

  const refreshDataAfterNewFileImport = (targetMappingType, newMappingData) => {
    setAllMappingData((prev) => ({
      ...prev,
      mappingCulture: {
        ...prev.mappingCulture,
        data: { ...prev.mappingCulture.data, [targetMappingType]: newMappingData },
      },
    }));
  };

  const IconAndTextButton = (icon, text) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const textButton = (text) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2">{text}</Typography>
    </Box>
  );

  const actions = [
    { icon: textButton(IMPORT_TYPE.geofolia), name: IMPORT_TYPE.geofolia },
    { icon: textButton(IMPORT_TYPE.telepac), name: IMPORT_TYPE.telepac },
    { icon: textButton(IMPORT_TYPE.apia), name: IMPORT_TYPE.apia },
    { icon: textButton(IMPORT_TYPE.wiuz), name: IMPORT_TYPE.wiuz },
  ];

  useEffect(() => {
    let rows = [];
    if (selectedMappingType === null) {
      setRows(rows);
      return;
    }
    if (cultureMappingData?.[selectedMappingType]?.length === 0) {
      setRows(rows);
      return;
    }
    rows =
      cultureMappingData?.[selectedMappingType]?.map((mappingData) => {
        return {
          id: mappingData.id,
          culture: mappingData.culture?.id || '',
          externalName: mappingData.externalName,
          irrigatedCulture: mappingData.irrigatedCulture,
          excludedCulture: mappingData.excludedCulture,
          mappingType: mappingData.mappingType,
        };
      }) || [];

    setRows(rows);
  }, [cultureMappingData, selectedMappingType]);

  const { getActions, rowModesModel, setRowModesModel, resetRowModesModel, RenderDeleteDialog } =
    useDataGridMappingActions(
      rows,
      cooperativeId,
      refreshDataAfterNewFileImport,
      cultureMappingData,
    );

  const columns = useMemo(() => {
    return [
      {
        field: 'externalName',
        headerName: 'Nom culture referentiel externe',
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'externalName'}
            name={t('label.externalName')}
          />
        ),
        cellClassName: 'culture-center-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'string',
        editable: true,
        preProcessEditCellProps: (params) => {
          const hasError = !params.props.value || params.props.value.trim().length < 1;
          return { ...params.props, error: hasError };
        },
        renderEditCell: (params) => {
          const hasError = !params.value || params.value.trim().length < 1;

          return (
            <TextField
              value={params.value || ''}
              onChange={(e) => {
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value,
                });
              }}
              error={hasError}
              helperText={hasError ? t('validator.minChar1') : ''}
              variant="standard"
              autoFocus
            />
          );
        },
      },
      {
        field: 'culture',
        headerName: 'Nom culture referentiel assolia',
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'culture'}
            name={t('common.culture')}
          />
        ),
        cellClassName: 'cropToPlant-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'singleSelect',
        valueOptions: cultureList?.map((culture) => ({
          ...culture,
          label: culture.value === '' ? t('message.noCultureSelected') : culture.label,
        })),
        valueFormatter: (params) => {
          return cultureList?.find((culture) => culture.value === params).label;
        },
        editable: true,
        sortComparator: (v1, v2) => {
          const label1 = cultureList?.find((culture) => culture.value === v1)?.label;
          const label2 = cultureList?.find((culture) => culture.value === v2)?.label;
          return label1.localeCompare(label2);
        },
        renderCell: (params) => {
          if (!params.value || params.value === '') {
            return t('message.noCultureSelected');
          }
          return params.formattedValue;
        },
      },
      {
        field: 'irrigatedCulture',
        headerName: 'Culture irriguee',
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'irrigatedCulture'}
            name={t('label.irrigated')}
          />
        ),
        cellClassName: 'cropToPlant-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'boolean',
        editable: true,
        valueFormatter: (value) => {
          if (value === true) return t('common.yes');
          if (value === false) return t('common.no');
          return value;
        },
      },
      {
        field: 'excludedCulture',
        headerName: "Exclue de l'assolement",
        headerClassName: 'cropToPlant-header',
        renderHeader: () => (
          <CustomSortableHeader
            setSortModel={setSortModel}
            fieldName={'excludedCulture'}
            name={t('label.excludedFromRotation')}
          />
        ),
        cellClassName: 'cropToPlant-cell',
        sortable: true,
        headerAlign: 'center',
        align: 'center',
        flex: 1,
        minWidth: 140,
        type: 'boolean',
        editable: true,
        valueFormatter: (value) => {
          if (value === true) return t('common.yes');
          if (value === false) return t('common.no');
          return value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: t('common.actions'),
        flex: 1,
        minWidth: 160,
        headerAlign: 'center',
        align: 'center',
        cellClassName: 'actions',
        getActions: getActions,
        renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
      },
    ];
  }, [cultureList, rowModesModel]);

  const processRowUpdate = useCallback(
    async (updatedRow, previousRow) => {
      const updatedRowMemory = { ...updatedRow };
      const mappingType = updatedRow.mappingType;
      const mappingDataId = updatedRow.id;
      const toUpdate = getOnlyAttributeUpdatedBetweenObjects(previousRow, updatedRow);

      if (Object.keys(toUpdate).length === 0) {
        return previousRow;
      }

      toUpdate.id = mappingDataId;

      if (updatedRowMemory.culture === '' && toUpdate.excludedCulture === false) {
        toast.warning(t('message.noCultureMappingConstraint'));
        return previousRow;
      }

      const apiPayload = { ...toUpdate };
      if ('culture' in apiPayload && apiPayload.culture === '') {
        apiPayload.culture = null;
      }

      try {
        const editedMappingData = await editCultureMappingData(
          mappingType,
          cooperativeId,
          apiPayload,
        );

        const responseData = { ...editedMappingData?.data?.data };
        if (responseData.culture === null) {
          responseData.culture = '';
        }

        const newMappingData = cultureMappingData?.[mappingType]?.map((mappingData) => {
          if (mappingData.id === mappingDataId) {
            return editedMappingData?.data?.data;
          }
          return mappingData;
        });

        refreshDataAfterNewFileImport(mappingType, newMappingData);
        toast.success(
          t('success.mappingUpdated', { name: editedMappingData?.data?.data.externalName }),
        );
        return updatedRow;
      } catch (error) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_UNEXPECTED'));
        return previousRow;
      }
    },
    [cultureMappingData],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SpeedDial
        ariaLabel="SpeedDial controlled open"
        icon={IconAndTextButton(<SpeedDialIcon />, t('button.importNewFile'))}
        FabProps={{
          variant: 'extended',
        }}
        open={open}
        onClick={() => setOpen(!open)}
        direction="right"
        sx={{ zIndex: 1 }}
      >
        {actions.map((action) => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            slotProps={{
              fab: {
                variant: 'extended',
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedMappingType(action.name);
              setIsImportModalOpen(true);
            }}
          />
        ))}
      </SpeedDial>
      {cultureMappingImportResponse.hasWarning && <AccordionErrorMappingImport />}
      <CustomCultureDataGrid
        fromMapping={true}
        theme={datagridMappingTheme}
        rows={rows}
        columns={columns}
        sortModel={sortModel}
        rowModesModel={rowModesModel}
        setRowModesModel={setRowModesModel}
        processRowUpdate={processRowUpdate}
        customToolbar={
          <CustomToolBarMappingDatagrid
            handleMappingTypeChange={handleMappingTypeChange}
            selectedMappingType={selectedMappingType}
            setModalCreateOpen={setModalCreateOpen}
          />
        }
      />
      {isImportModalOpen && (
        <ImportMappingFileModal
          open={isImportModalOpen}
          setModalOpen={setIsImportModalOpen}
          selectedMappingType={selectedMappingType}
          cooperativeId={cooperativeId}
          setOpenImportButton={setOpen}
          refreshDataAfterNewFileImport={refreshDataAfterNewFileImport}
        />
      )}
      {isModalCreateOpen && (
        <AddCultureMappingDataModal
          isModalCreateOpen={isModalCreateOpen}
          setModalCreateOpen={setModalCreateOpen}
          cultureList={cultureList}
          cooperativeId={cooperativeId}
          mappingTypes={mappingTypes}
          selectedMappingType={selectedMappingType}
          actions={actions}
          refreshDataAfterNewFileImport={refreshDataAfterNewFileImport}
          cultureMappingData={cultureMappingData}
        />
      )}
      <RenderDeleteDialog />
    </Box>
  );
};
export default MappingCulture;
